import { ReactNode, useEffect, useRef } from 'react';
import { ELEMENT_HEIGHT_DEFAULT, MAX_ITEMS_ON_DISPLAY } from 'shared/constants/dropdown';

/**
 * Props for the SearchableSuggestionList component.
 */
interface SearchableSuggestionListProps {
  /**
   * Index of the active suggestion.
   */
  activeSuggestionIndex: number;
  /**
   * Data attribute for testing.
   */
  dataQa: string;
  /**
   * Height of each element in the suggestion list.
   */
  elementHeight?: number;
  /**
   * Array of filtered suggestion nodes.
   */
  filteredSuggestionNodes: ReactNode[];
  /**
   * Maximum number of items on display.
   */
  maxItemsOnDisplay?: number;
  /**
   * Reference to the suggestion list items being passed in through filteredSuggestionNodes.
   */
  suggestionItemRef: React.RefObject<HTMLDivElement>;
}

/**
 * Component representing a list of suggestions.
 * @param activeSuggestionIndex - Index of the active suggestion.
 * @param dataQa - Data attribute for testing.
 * @param elementHeight - Height of each element in the suggestion list.
 * @param filteredSuggestionNodes - Array of filtered suggestions nodes.
 * @param maxItemsOnDisplay - Maximum number of items on display.
 * @param suggestionItemRef - Reference to the suggestion list items being passed in through filteredSuggestionNodes.
 * @returns JSX.Element representing the SearchableSuggestionList component.
 */
const SearchableSuggestionList: React.FC<SearchableSuggestionListProps> = ({
  activeSuggestionIndex,
  dataQa,
  elementHeight = ELEMENT_HEIGHT_DEFAULT,
  filteredSuggestionNodes,
  maxItemsOnDisplay = MAX_ITEMS_ON_DISPLAY,
  suggestionItemRef,
}) => {
  const suggestionListContainerRef = useRef<HTMLDivElement>(null);
  // Scrolls to the target element at the top of the list whenever we reach the bottom of said list.
  // This creates a paginated look - please see LMS1-4967.
  useEffect(() => {
    if (suggestionListContainerRef.current && suggestionItemRef.current) {
      const containerRect = suggestionListContainerRef.current.getBoundingClientRect();
      const elementRect = suggestionItemRef.current.getBoundingClientRect();
      // If the active element goes below or above the view of the container element, scroll to the top of the selected/highlighted element.
      if (elementRect.bottom > containerRect.bottom || elementRect.top < containerRect.top) {
        const menuItemRefTop = suggestionItemRef?.current?.offsetTop || 0;
        suggestionListContainerRef.current.scrollTo({ top: menuItemRefTop });
      }
    }
  }, [activeSuggestionIndex, suggestionItemRef]);

  const adjustListHeight = () => {
    const dropdown = suggestionListContainerRef?.current?.getBoundingClientRect();
    const minHeight = 3 * elementHeight;
    const defaultHeight = maxItemsOnDisplay * elementHeight + 80;
    const viewportHeight = window.innerHeight;
    if (!suggestionListContainerRef.current || !dropdown?.top) {
      return `${defaultHeight}px`;
    }

    const availableSpace = viewportHeight - dropdown.top - 10; // -10 is for dropdown bottom visibility

    /**
     * return minHeight if availableSpace is lower than height of 3 items
     * return maxHeight if availableSpace is higher than default maxHeight which is maxItemsOnDisplay * elementHeight
     * added 80px for more visibility
     */
    return `${Math.min(Math.max(minHeight, availableSpace), defaultHeight)}px`;
  };
  return (
    <div
      className="z-50 absolute bg-white mt-1 py-1 border rounded-md shadow-sm w-full max-h-72 overflow-y-auto"
      data-qa={`${dataQa}List`}
      data-testid={`${dataQa}List`}
      ref={suggestionListContainerRef}
      style={{
        maxHeight: adjustListHeight(),
      }}
    >
      {filteredSuggestionNodes}
    </div>
  );
};

export default SearchableSuggestionList;
